/* eslint-disable eqeqeq */
/* eslint-disable array-callback-return */
import { Motion, spring } from "react-motion";
import { Box, Button, HStack, Skeleton, Stack, Text } from "@chakra-ui/react";
import React, { useState, useRef, useLayoutEffect, useEffect } from "react";
import AppColors from "../../../constants/AppColors";
import PerfectScrollbar from "react-perfect-scrollbar"
import { defaultStyle, REBUTTAL_STATUS, RESOLVED_STATUS } from "../constants";
import { MentionsInput, Mention } from 'react-mentions'
import { formatDistanceStrict } from "date-fns";
import { AuditSheetContext } from "../../../providers/AuditSheetProvider";
import { useContext } from "react";
import RenderMessage from "./RenderMessage";
import { RebuttalIcon } from "../../../constants/IconData";
import { successToast } from "../../../components/successToast";
import { useToastr } from "@laxmimanogna/code-quick-components";


const CommentBox = ({ showMenu, metaData, closeComment, sheet, batch, coords }) => {
    const toast = useToastr();
    const auditSheetContext = useContext(AuditSheetContext);
    const { users, isPostCommentLoading, comments, setComments, isCommentLoading, postComment, commentFlags, setCommentFlags, allUsers, allComments, setAllComments, currentUpload, setCurrentUpload, batchChartStatuses } =
        auditSheetContext;
    const { selection, rowIndex, selectedRowData } = metaData

    // Only show row-level comments (audit_sheet_columns is empty)
    const rowComments = comments.filter(c => !c.audit_sheet_columns?.length && !['ADD', 'DELETE'].includes(c.action));
    // Always work off the latest thread for current status
    const latestThread = rowComments.length > 0 ? rowComments[rowComments.length - 1] : null;

    const [message, setMessage] = useState('')

    const boxRef = useRef(null);
    const scrollbarRef = useRef(null);
    const [boxPosition, setBoxPosition] = useState({ x: -9999, y: -9999 });

    // Auto-scroll to bottom when thread updates
    useEffect(() => {
        if (scrollbarRef.current) {
            scrollbarRef.current.scrollTop = scrollbarRef.current.scrollHeight;
        }
    }, [rowComments]);

    useLayoutEffect(() => {
        if (!showMenu) {
            setBoxPosition({ x: -9999, y: -9999 });
            return;
        }
        if (boxRef.current) {
            const rect = boxRef.current.getBoundingClientRect();
            const vw = window.innerWidth;
            const vh = window.innerHeight;
            const MARGIN = 10;

            let x = coords.x + 20;
            let y = coords.y;

            if (x + rect.width > vw - MARGIN) x = vw - rect.width - MARGIN;
            if (x < MARGIN) x = MARGIN;
            if (y + rect.height > vh - MARGIN) y = coords.y - rect.height - 5;
            if (y < MARGIN) y = MARGIN;

            setBoxPosition({ x, y });
        }
    }, [showMenu, coords]);

    const getStatus = () => {
        if (!latestThread) return 'Open';
        const replies = latestThread.reply_comments || [];
        let status = 'Open';
        for (const reply of replies) {
            const action = reply.action;
            if (action === 'REBUTTAL-ACCEPTED') status = 'Accepted';
            else if (action === 'REBUTTAL-REJECTED') status = 'Rejected';
            else if (action === 'REBUTTAL-RESOLVED') status = 'Resolved';
            else if (RESOLVED_STATUS.includes(action)) status = 'Resolved';
            else if (action === 'RE-OPENED') status = 'Re-opened';
        }
        return status;
    }

    const getHeaderBg = () => {
        const s = getStatus();
        if (s === 'Rejected' || s === 'Resolved') return AppColors.darkGray;
        return AppColors.secondary;
    }

    const getStatusBadgeBg = () => {
        const s = getStatus();
        if (s === 'Accepted') return '#16a34a';
        if (s === 'Rejected') return AppColors.danger;
        if (s === 'Resolved') return 'rgba(255,255,255,0.15)';
        return 'rgba(255,255,255,0.25)';
    }

    // In batch view currentUpload is always {} — use per-chart status from the statuses map instead
    const chartStatus = batch
        ? batchChartStatuses?.[String(selectedRowData?.chart_id)]?.status
        : currentUpload?.status;
    const isChartUnderReview =
        chartStatus === 'REBUTTAL IN PROGRESS' ||
        chartStatus === 'CLIENT REBUTTAL';

    const isFirstMessage = rowComments.length === 0;
    const threadStatus = getStatus();
    const isRejected = threadStatus === 'Rejected';
    const isClosed = threadStatus === 'Resolved';
    const isAccepted = threadStatus === 'Accepted';
    // Accepted means QA acknowledged the rebuttal and correction is in progress —
    // client should reply to the same thread, not open a new one.
    // Only rejected or resolved threads warrant starting fresh.
    const shouldStartNewThread = isRejected || isClosed;
    // Always allow a new rebuttal when no active open thread exists for this row
    const isNewRootThread = isFirstMessage || shouldStartNewThread;
    // Thread is open and being worked on — client adds a reply, not a new rebuttal
    const alreadyInClientReview = !isFirstMessage && !shouldStartNewThread;

    const onSubmit = async () => {
        // Use selectedRowData (from filtered Handsontable view) so the correct row
        // is picked even when a renderer/date filter is active and visual row index
        // no longer matches the unfiltered sheet.data index.
        const rowId = selectedRowData?.id;
        const chartId = selectedRowData?.chart_id;
        const validRow = rowId ? [rowId] : [];
        if (!validRow.length) {
            toast.showError({ description: 'Please select a valid row.' });
            return;
        }
        if (!chartId) {
            toast.showError({ description: 'Please select a valid row.' });
            return;
        }

        const body = {
            audit_sheet_rows: validRow,
            audit_sheet_columns: [],
            action: isNewRootThread ? REBUTTAL_STATUS : 'OPEN',
            comment: message,
            chart: chartId,
            tagged_user: [],
        };

        if (!isNewRootThread && latestThread) {
            body.parent = latestThread.id;
        }

        // Handle @mentions
        const strArray = message.match(/@\[(.\d*?)]/g);
        if (strArray?.length) {
            const tagged = [];
            strArray.forEach(str => {
                const match = str.match(/@\[(.\d*?)]/);
                if (match && !tagged.includes(match[1])) tagged.push(Number(match[1]));
            });
            body.tagged_user = tagged;
        }

        const response = await postComment(body);
        if (response?.id) {
            const commentsUpdater = prev => {
                if (response.parent) {
                    return prev.map(c =>
                        c.id === response.parent
                            ? { ...c, reply_comments: [...(c.reply_comments || []), response] }
                            : c
                    );
                }
                return [...prev, { ...response, reply_comments: [] }];
            };
            setComments(commentsUpdater);

            // For allComments: if adding a reply and parent exists in allComments, attach it.
            // If parent is absent (drawer loaded a different sheet), add the full root comment.
            setAllComments(prev => {
                if (response.parent) {
                    const parentExists = prev.some(c => c.id === response.parent);
                    if (parentExists) {
                        return prev.map(c =>
                            c.id === response.parent
                                ? { ...c, reply_comments: [...(c.reply_comments || []), response] }
                                : c
                        );
                    }
                    // Parent not in allComments — add the full root thread from comments state
                    const rootComment = latestThread;
                    if (rootComment) {
                        const rootWithReply = {
                            ...rootComment,
                            reply_comments: [...(rootComment.reply_comments || []), response],
                        };
                        return [rootWithReply, ...prev];
                    }
                    return prev;
                }
                return [...prev, { ...response, reply_comments: [] }];
            });

            // Update row-level flag (immutable)
            const updatedFlags = { ...commentFlags };
            validRow.forEach(rowId => {
                updatedFlags[rowId] = { ...(commentFlags[rowId] || {}), '__row__': true };
            });
            setCommentFlags(updatedFlags);

            if (!batch && currentUpload.status !== 'CLIENT REBUTTAL' && currentUpload.status !== 'REBUTTAL IN PROGRESS') {
                setCurrentUpload(prev => ({ ...prev, status: 'CLIENT REBUTTAL' }));
            }
            successToast(toast, {
                header: 'Success!',
                description: isNewRootThread ? 'Rebuttal sent for review.' : 'Reply sent.',
            });
            setMessage('');
            closeComment();
        }
    };

    const renderCommentBody = (comment, key, isFirst = false) => {
        const fullName = `${comment.user.first_name} ${comment.user.last_name}`;
        const isStatus = !comment.comment;
        const STATUS_LABEL = {
            'RE-OPENED': 'Re-opened',
            'RESOLVED': 'Marked as resolved',
            'REBUTTAL-RESOLVED': 'Resolved',
            'REBUTTAL-ACCEPTED': 'Accepted rebuttal',
            'REBUTTAL-REJECTED': 'Rejected rebuttal',
        };
        return (
            <Box
                key={key}
                px={3} py={2}
                borderTop={isFirst ? 'none' : '1px solid'}
                borderColor='gray.100'
            >
                <HStack spacing={1} align='baseline' mb={isStatus ? 0 : 1}>
                    <Text fontSize='sm' fontWeight='semibold' color={AppColors.secondary} lineHeight='1.3'>
                        {fullName}
                    </Text>
                    <Text fontSize='xs' color='gray.400'>
                        {comment.commented_date
                            ? formatDistanceStrict(new Date(comment.commented_date), new Date(), { addSuffix: true })
                            : ''}
                    </Text>
                    {comment.action === 'REBUTTAL' && <RebuttalIcon style={{ width: 13, height: 13 }} />}
                </HStack>
                {isStatus
                    ? <Text as='i' fontSize='xs' color={AppColors.darkGray}>{STATUS_LABEL[comment.action]}</Text>
                    : <RenderMessage comment={comment} allUsers={allUsers} />
                }
            </Box>
        );
    };
    
    const renderLoader = () => (
        <Stack px={2} py={1}>
            <Skeleton height='12px' width='80px' />
            <Skeleton height='12px' />
            <Skeleton height='12px' width='60px' mt={3} />
            <Skeleton height='12px' />
        </Stack>
    );

    return (
        <Motion defaultStyle={{ opacity: 0 }} style={{ opacity: !showMenu ? spring(0) : spring(1) }}>
            {(interpolatedStyle) => (
                <>
                    {showMenu && (
                        <Box
                            ref={boxRef}
                            zIndex={1000}
                            bg='white'
                            overflow='hidden'
                            border='1px solid'
                            borderColor='gray.200'
                            style={{
                                borderRadius: '12px',
                                position: 'fixed',
                                top: boxPosition.y,
                                left: boxPosition.x,
                                opacity: interpolatedStyle.opacity,
                                width: 'clamp(280px, 25vw, 360px)',
                                boxShadow: '0 4px 16px rgba(0,0,0,0.10)',
                            }}
                        >
                            {/* Compact single-row header — mirrors drawer CommentBox */}
                            <HStack px={3} py={1.5} bg={getHeaderBg()} justifyContent='space-between'>
                                <HStack spacing={2}>
                                    <Box px={2} py='1px' bg='rgba(0,0,0,0.18)' borderRadius='full' flexShrink={0}>
                                        <Text fontSize='10px' fontWeight='bold' color='white' letterSpacing='wide' whiteSpace='nowrap'>
                                            REBUTTAL
                                        </Text>
                                    </Box>
                                    {rowIndex != null && (
                                        <Text fontSize='xs' color='rgba(255,255,255,0.8)'>Row {rowIndex + 1}</Text>
                                    )}
                                </HStack>
                                <HStack spacing={1.5}>
                                    {rowComments.length > 0 && (
                                        <Box px={2} py='1px' borderRadius='full' bg={getStatusBadgeBg()}>
                                            <Text fontSize='10px' fontWeight='bold' color='white' whiteSpace='nowrap'>
                                                {threadStatus}
                                            </Text>
                                        </Box>
                                    )}
                                    <Box
                                        cursor='pointer'
                                        onClick={closeComment}
                                        color='rgba(255,255,255,0.8)'
                                        _hover={{ color: 'white' }}
                                        fontSize='sm'
                                        lineHeight={1}
                                        fontWeight='bold'
                                        px={1}
                                    >
                                        ✕
                                    </Box>
                                </HStack>
                            </HStack>

                            {/* Thread */}
                            {isCommentLoading
                                ? <Box p={3}>{renderLoader()}</Box>
                                : rowComments.length > 0 && (
                                    <PerfectScrollbar
                                        containerRef={el => { scrollbarRef.current = el; }}
                                        style={{ maxHeight: '180px' }}
                                    >
                                        {rowComments.map((c, i) => (
                                            <React.Fragment key={c.id ?? i}>
                                                {renderCommentBody(c, `${i}-root`, i === 0)}
                                                {c.reply_comments?.map((r, j) => renderCommentBody(r, `${i}-${j}`, false))}
                                            </React.Fragment>
                                        ))}
                                    </PerfectScrollbar>
                                )
                            }

                            {/* Input section */}
                            <Box
                                px={3} pb={3} pt={2}
                                borderTop={rowComments.length > 0 && !isCommentLoading ? '1px solid' : 'none'}
                                borderColor='gray.100'
                            >
                                {isRejected && (
                                    <HStack mb={2} pl={3} py='3px' borderLeft='3px solid' borderColor={AppColors.danger} spacing={1}>
                                        <RebuttalIcon style={{ height: 11, width: 11, flexShrink: 0 }} />
                                        <Text fontSize='xs' fontWeight='semibold' color={AppColors.danger}>
                                            Previous rebuttal was rejected — new rebuttal will be sent to QA
                                        </Text>
                                    </HStack>
                                )}
                                {isClosed && (
                                    <HStack mb={2} pl={3} py='3px' borderLeft='3px solid' borderColor={AppColors.secondary} spacing={1}>
                                        <RebuttalIcon style={{ height: 11, width: 11, flexShrink: 0 }} />
                                        <Text fontSize='xs' fontWeight='semibold' color={AppColors.secondary}>
                                            Previous rebuttal resolved — new rebuttal will be sent to QA
                                        </Text>
                                    </HStack>
                                )}
                                {alreadyInClientReview && (
                                    <HStack mb={2} pl={3} py='3px' borderLeft='3px solid' borderColor='#d97706' spacing={1}>
                                        <Text fontSize='xs' fontWeight='semibold' color='#b7791f'>
                                            Under QA review — reply will be added to existing thread
                                        </Text>
                                    </HStack>
                                )}
                                {isFirstMessage && isChartUnderReview && (
                                    <HStack mb={2} pl={3} py='3px' borderLeft='3px solid' borderColor={AppColors.secondary} spacing={1}>
                                        <RebuttalIcon style={{ height: 11, width: 11, flexShrink: 0 }} />
                                        <Text fontSize='xs' fontWeight='semibold' color={AppColors.secondary}>
                                            Chart is under QA review — new rebuttal will be added
                                        </Text>
                                    </HStack>
                                )}
                                {isFirstMessage && !isChartUnderReview && (
                                    <HStack mb={2} pl={3} py='3px' borderLeft='3px solid' borderColor={AppColors.secondary} spacing={1}>
                                        <RebuttalIcon style={{ height: 11, width: 11, flexShrink: 0 }} />
                                        <Text fontSize='xs' fontWeight='semibold' color={AppColors.secondary}>
                                            Chart will be sent to QA for review
                                        </Text>
                                    </HStack>
                                )}
                                <MentionsInput
                                    placeholder={isNewRootThread ? 'Write your rebuttal...' : 'Add a message...'}
                                    value={message}
                                    onChange={e => setMessage(e.target.value)}
                                    style={{ ...defaultStyle, control: { ...defaultStyle.control, width: '100%' } }}
                                    allowSpaceInQuery
                                >
                                    <Mention
                                        displayTransform={id => {
                                            const u = users.find(u => u.id == id);
                                            return u ? `@${u.first_name} ${u.last_name}` : `@${id}`;
                                        }}
                                        trigger='@'
                                        data={users}
                                        markup='@[__id__]'
                                        style={{ fontWeight: 500, backgroundColor: AppColors.bgColor }}
                                    />
                                </MentionsInput>
                                <Button
                                    mt={2} w='100%' size='sm'
                                    bgColor={AppColors.secondary}
                                    borderRadius='md' color='white'
                                    isDisabled={!message}
                                    isLoading={isPostCommentLoading}
                                    onClick={onSubmit}
                                    _hover={{ bgColor: AppColors.secondary, opacity: 0.9 }}
                                >
                                    {isNewRootThread ? 'Submit Rebuttal' : 'Send'}
                                </Button>
                            </Box>
                        </Box>
                    )}
                </>
            )}
        </Motion>
    );
};
export default CommentBox
